import type { Config } from '@gmn-portal/backend';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import { Check, ChevronDown, X } from 'lucide-react';
import type * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useDebounce } from 'use-debounce';
import type { Where } from '@/core/api';
import { payloadClient } from '@/core/api';
import { useMediaBlobUrl } from '@/core/api/media';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
} from '@/shared/components/ui';
import { cn } from '@/shared/utils';

export type MultiComboBoxItemType = {
  value: string;
  label: string;
  imageId?: string | null;
};

type MultiSearchComboboxProps<T extends keyof Config['collections']> = {
  value?: string[];
  onSelect: (value: string[]) => void;
  className?: string;
  collectionKey: T;
  searchKey: keyof Config['collections'][T];
  imageKey?: keyof Config['collections'][T];
  excludeIds?: string[];
  error?: string;
  autoFocus?: boolean;
  texts?: {
    selectItemMsg?: string;
    searchPlaceholder?: string;
    noResultsMsg?: string;
    searchingMsg?: string;
    errorMsg?: string;
    loadMoreMsg?: string;
    clearAllMsg?: string;
  };
  icon?: LucideIcon;
  xs?: boolean;
  customWhere?: Where;
  disabled?: boolean;
  maxItems?: number;
};

const popOverStyles = {
  width: 'var(--radix-popover-trigger-width)',
};

const ITEMS_PER_PAGE = 10;

const buildUserVisibilityWhere = <T extends keyof Config['collections']>(
  collectionKey: T
): Where | null => (collectionKey === 'users' ? { archived: { not_equals: true } } : null);

export function MultiSearchCombobox<T extends keyof Config['collections']>({
  value = [],
  onSelect,
  className,
  collectionKey,
  searchKey,
  imageKey,
  excludeIds = [],
  error,
  autoFocus = false,
  texts,
  icon: Icon,
  xs = false,
  customWhere,
  disabled = false,
  maxItems,
}: MultiSearchComboboxProps<T>) {
  const { t } = useIntl();

  const defaultTexts = {
    selectItemMsg: t('searchCombobox.selectItemMsg'),
    searchPlaceholder: t('common.search'),
    noResultsMsg: t('common.noResults'),
    searchingMsg: t('searchCombobox.searchingMsg'),
    errorMsg: t('errors.generalError'),
    loadMoreMsg: t('common.loadMore'),
    clearAllMsg: t('common.clearAll'),
  };

  const finalTexts = { ...defaultTexts, ...texts };

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

  const { ref: loadMoreRef, inView } = useInView();
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch selected items by IDs
  const { data: selectedItems = [] } = useQuery({
    queryKey: [collectionKey, 'byIds', value],
    queryFn: async () => {
      if (!value?.length) return [];
      const items = await Promise.all(
        value.map(async (id) => {
          try {
            const item = await payloadClient.findById({
              collection: collectionKey,
              id,
            });
            return {
              value: item.id,
              label: item[searchKey] as string,
              imageId: imageKey ? (item[imageKey] as string) : undefined,
            } as MultiComboBoxItemType;
          } catch {
            return null;
          }
        })
      );
      return items.filter(Boolean) as MultiComboBoxItemType[];
    },
    enabled: !!value?.length,
    placeholderData: keepPreviousData,
  });

  const filteredSelectedItems = useMemo(() => {
    // Always filter to only show items that are currently selected
    // This handles both placeholder data and fresh data correctly
    return selectedItems.filter((item) => value.includes(item.value));
  }, [selectedItems, value]);

  // Search for items
  const {
    data: searchResults,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      collectionKey,
      searchKey,
      debouncedSearchQuery,
      [...excludeIds, ...value],
      customWhere,
      imageKey,
    ],
    queryFn: async ({ pageParam }) => {
      const baseWhere: Where = {
        and: [
          ...(debouncedSearchQuery
            ? [
                {
                  [searchKey]: {
                    like: debouncedSearchQuery,
                  },
                },
              ]
            : []),
          {
            id: {
              not_in: [...excludeIds, ...value],
            },
          },
        ],
      };

      const userVisibilityWhere = buildUserVisibilityWhere(collectionKey);
      if (userVisibilityWhere) {
        baseWhere.and = [...(baseWhere.and ?? []), userVisibilityWhere];
      }

      const finalWhere = customWhere ? { and: [baseWhere, customWhere] } : baseWhere;

      const response = await payloadClient.find({
        collection: collectionKey,
        where: finalWhere,
        limit: ITEMS_PER_PAGE,
        page: pageParam,
      });

      return {
        items: response.docs.map((item) => ({
          value: item.id,
          label: item[searchKey] as string,
          imageId: imageKey ? (item[imageKey] as string) : undefined,
        })),
        nextPage: response.hasNextPage ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: open,
    initialPageParam: 1,
    placeholderData: keepPreviousData,
  });

  // Handle infinite scroll
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Handle autofocus
  useEffect(() => {
    if (autoFocus && !disabled && open) {
      inputRef.current?.focus();
    }
  }, [autoFocus, disabled, open]);

  // Handle item selection
  const handleSelect = useCallback(
    (selectedValue: string) => {
      const isSelected = value.includes(selectedValue);
      if (isSelected) {
        onSelect(value.filter((id) => id !== selectedValue));
      } else {
        if (maxItems && value.length >= maxItems) {
          return; // Don't add if max items reached
        }
        onSelect([...value, selectedValue]);
      }
    },
    [onSelect, value, maxItems]
  );

  // Handle removing specific item
  const handleRemoveItem = useCallback(
    (e: React.MouseEvent, itemValue: string) => {
      e.preventDefault();
      e.stopPropagation();
      onSelect(value.filter((id) => id !== itemValue));
    },
    [onSelect, value]
  );

  // Handle clearing all items
  const handleClearAll = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onSelect([]);
    },
    [onSelect]
  );

  // Format display text for the trigger button
  const displayText = useMemo(() => {
    if (!filteredSelectedItems.length) {
      return (
        <div className="flex items-center gap-2 text-muted-foreground">
          {Icon && <Icon className={cn('shrink-0', xs ? 'size-3.5' : 'size-4')} />}
          <span className="truncate">{finalTexts.selectItemMsg}</span>
        </div>
      );
    }

    const firstItem = filteredSelectedItems[0];
    return (
      <div className="flex items-center gap-2 truncate">
        <ItemContent item={firstItem} icon={Icon} xs={xs} showImage={!!imageKey} />
        {filteredSelectedItems.length > 1 && (
          <span className="inline-flex items-center rounded-md bg-secondary text-secondary-foreground px-1.5 py-0.5 text-xs font-medium">
            +{filteredSelectedItems.length - 1}
          </span>
        )}
      </div>
    );
  }, [filteredSelectedItems, Icon, xs, imageKey, finalTexts.selectItemMsg]);

  const flattenedResults = searchResults?.pages.flatMap((page) => page.items) || [];

  return (
    <Popover open={open && !disabled} onOpenChange={(isOpen) => !disabled && setOpen(isOpen)}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between',
            className,
            error && 'border-red-500',
            xs && 'h-8'
          )}
          disabled={disabled}
        >
          <div className="flex items-center truncate w-full mr-2">{displayText}</div>
          <ChevronDown className={cn('shrink-0 opacity-50', xs ? 'size-3.5' : 'size-4')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent style={popOverStyles} className="p-0" align="start">
        {/* Header with clear all button */}
        <div className="px-3 pt-3 pb-2 flex items-center justify-between">
          <span className="text-sm font-medium">{finalTexts.selectItemMsg}</span>
          {filteredSelectedItems.length > 0 && (
            <Button variant="ghost" size="sm" className="h-5 px-2 text-xs" onClick={handleClearAll}>
              {finalTexts.clearAllMsg}
            </Button>
          )}
        </div>

        {/* Selected items display */}
        {filteredSelectedItems.length > 0 && (
          <div className="px-3 pb-2">
            <div className="flex flex-wrap gap-1">
              {filteredSelectedItems.map((item) => (
                <div
                  key={item.value}
                  className={`
                    inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium bg-secondary text-secondary-foreground
                    gap-1
                  `}
                >
                  <ItemContent
                    item={item}
                    showImage={!!imageKey}
                    xs
                    truncate
                    maxWidth="max-w-[120px]"
                    textColor="text-secondary-foreground"
                  />
                  <X
                    className="size-3 cursor-pointer shrink-0 hover:text-destructive"
                    onClick={(e) => handleRemoveItem(e, item.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="border-t" />

        {/* Search and results */}
        <Command shouldFilter={false}>
          <CommandInput
            ref={inputRef}
            placeholder={finalTexts.searchPlaceholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
            className="border-0"
          />
          <ScrollArea className="max-h-[200px]">
            <CommandList>
              {isLoading ? (
                <CommandEmpty>{finalTexts.searchingMsg}</CommandEmpty>
              ) : isError ? (
                <CommandEmpty>{finalTexts.errorMsg}</CommandEmpty>
              ) : (
                <CommandEmpty>{finalTexts.noResultsMsg}</CommandEmpty>
              )}
              <CommandGroup>
                {flattenedResults.map((item) => {
                  const isSelected = value.includes(item.value);
                  const isMaxReached = maxItems && value.length >= maxItems && !isSelected;

                  return (
                    <CommandItem
                      key={item.value}
                      value={item.value}
                      onSelect={() => !isMaxReached && handleSelect(item.value)}
                      disabled={!!isMaxReached}
                      className={cn(isMaxReached && 'opacity-50 cursor-not-allowed')}
                    >
                      <div className="flex items-center w-full">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <ItemContent item={item} showImage={!!imageKey} />
                        </div>
                        {isSelected && <Check className="ml-auto size-4 flex-none" />}
                      </div>
                    </CommandItem>
                  );
                })}
                {hasNextPage && (
                  <CommandItem ref={loadMoreRef} disabled>
                    <div className="w-full text-center text-sm text-muted-foreground">
                      {isFetchingNextPage ? t('common.loading') : finalTexts.loadMoreMsg}
                    </div>
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </ScrollArea>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ItemContent({
  item,
  icon: Icon,
  xs = false,
  showImage = false,
  truncate = false,
  maxWidth,
  textColor = 'text-foreground',
}: {
  item: MultiComboBoxItemType;
  icon?: LucideIcon;
  xs?: boolean;
  showImage?: boolean;
  truncate?: boolean;
  maxWidth?: string;
  textColor?: string;
}) {
  const { data: blobUrl } = useMediaBlobUrl(showImage && item.imageId ? item.imageId : null);

  return (
    <>
      {showImage && item.imageId && blobUrl ? (
        <img
          src={blobUrl}
          alt=""
          className={cn('rounded object-cover shrink-0', xs ? 'size-4' : 'size-5')}
        />
      ) : Icon ? (
        <Icon className={cn('shrink-0 text-muted-foreground', xs ? 'size-3' : 'size-4')} />
      ) : null}
      <span className={cn(textColor, truncate && 'truncate', maxWidth)} title={item.label}>
        {item.label}
      </span>
    </>
  );
}
